import { useState, useRef, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Camera, Upload, Loader2, Check, FileImage, FileText, X, Plus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { calculateNextServiceDate } from "@/lib/dateUtils";
import { cn } from "@/lib/utils";
import { useFileUpload } from "@/hooks/useFileUpload";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { toast } from "@/hooks/use-toast";

const AddFurnitureSheet = ({ open, onOpenChange, onSuccess, initialData = null, onSave = null }) => {
    const { user } = useAuth();
    const { activeProperty } = usePropertyContext();
    const { uploading, uploadFile, capturePhoto, selectFile } = useFileUpload();

    const [loading, setLoading] = useState(false);
    const [name, setName] = useState("");
    const [brand, setBrand] = useState("");
    const [notes, setNotes] = useState("");
    const [purchaseDate, setPurchaseDate] = useState("");
    const [purchasePrice, setPurchasePrice] = useState("");
    const [warrantyExpiry, setWarrantyExpiry] = useState("");
    const [invoiceFile, setInvoiceFile] = useState(null);
    const [warrantyFile, setWarrantyFile] = useState(null);
    const [maintenanceTasks, setMaintenanceTasks] = useState([]);
    const scrollRef = useRef(null);

    // Populate form if editing
    useEffect(() => {
        if (open) {
            if (initialData) {
                setName(initialData.name || "");
                setBrand(initialData.brand || "");
                setNotes(initialData.notes || "");
                setPurchaseDate(initialData.purchase_date || "");
                setPurchasePrice(initialData.purchase_price?.toString() || "");
                setWarrantyExpiry(initialData.warranty_expiry || "");
                setInvoiceFile(initialData.receipt_url ? { url: initialData.receipt_url, name: "Proof of Purchase", type: "image/jpeg" } : null);
                setWarrantyFile(initialData.warranty_url ? { url: initialData.warranty_url, name: "Warranty Doc", type: "image/jpeg" } : null);
                setMaintenanceTasks(initialData.maintenance_tasks || []);
            } else {
                resetForm();
            }
        }
    }, [open, initialData]);

    // Scroll to focused input for keyboard avoidance
    const handleFocus = (e) => {
        setTimeout(() => {
            e.target.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 350);
    };

    const handleCameraCapture = async (type) => {
        const file = await capturePhoto();
        if (file) {
            const result = await uploadFile(file, "house-vault"); // Using house-vault bucket
            if (result) {
                if (type === 'invoice') setInvoiceFile(result);
                else setWarrantyFile(result);
            }
        }
    };

    const handleFileSelect = async (type) => {
        const file = await selectFile("application/pdf,image/*");
        if (file) {
            const result = await uploadFile(file, "receipts");
            if (result) {
                if (type === 'invoice') setInvoiceFile(result);
                else setWarrantyFile(result);
            }
        }
    };

    const resetForm = () => {
        setName("");
        setBrand("");
        setNotes("");
        setPurchaseDate("");
        setPurchasePrice("");
        setWarrantyExpiry("");
        setInvoiceFile(null);
        setWarrantyFile(null);
        setMaintenanceTasks([]);
    };

    const handleSave = async () => {
        if (!name.trim()) {
            toast({ title: "Name Required", description: "Please enter a name for the item.", variant: "destructive" });
            return;
        }

        setLoading(true);
        try {
            // Process and calculate next service date for each subtask
            const processedTasks = maintenanceTasks.map(task => {
                const base = task.last_service_date || purchaseDate || new Date().toISOString().split("T")[0];
                const next = calculateNextServiceDate(base, task.frequency || 1, task.frequency_unit || "months");
                return {
                    ...task,
                    frequency: task.frequency ? parseInt(task.frequency, 10) : 1,
                    last_service_date: task.last_service_date || purchaseDate || new Date().toISOString().split("T")[0],
                    next_service_date: next
                };
            });

            const itemData = {
                name: name.trim(),
                brand: brand.trim() || null,
                notes: notes.trim() || null,
                purchase_date: purchaseDate || null,
                purchase_price: purchasePrice ? parseFloat(purchasePrice) : null,
                warranty_expiry: warrantyExpiry || null,
                receipt_url: invoiceFile?.url || null,
                warranty_url: warrantyFile?.url || null,
                maintenance_tasks: processedTasks
            };

            if (initialData && onSave) {
                const success = await onSave(itemData);
                if (success) {
                    onOpenChange(false);
                    onSuccess?.();
                }
                return;
            }

            const { error } = await supabase.from("furniture").insert({
                user_id: user.id,
                property_id: activeProperty?.id,
                ...itemData
            });

            if (error) throw error;

            toast({ title: "Furniture Added", description: `${name} has been added to your home.` });
            resetForm();
            onOpenChange(false);
            onSuccess?.();
        } catch (error) {
            console.error("Error saving furniture:", error);
            toast({ title: "Error", description: "Failed to save item.", variant: "destructive" });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = (open) => {
        if (!open) resetForm();
        onOpenChange(open);
    };

    const renderFileUpload = (label, file, type) => (
        <div className="space-y-2">
            <Label className="text-sm font-medium text-foreground">{label}</Label>
            {!file ? (
                <div className={cn(
                    "w-full border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-2 py-4 transition-colors",
                    uploading ? "opacity-50 pointer-events-none bg-muted/20" : "hover:border-primary/50 hover:bg-accent/50"
                )}>
                    {uploading ? (
                        <Loader2 className="w-5 h-5 text-primary animate-spin" />
                    ) : (
                        <div className="flex gap-3">
                            <button type="button" onClick={() => handleCameraCapture(type)} className="p-2 rounded-full bg-primary/10 hover:bg-primary/20">
                                <Camera className="w-5 h-5 text-primary" />
                            </button>
                            <button type="button" onClick={() => handleFileSelect(type)} className="p-2 rounded-full bg-primary/10 hover:bg-primary/20">
                                <Upload className="w-5 h-5 text-primary" />
                            </button>
                        </div>
                    )}
                    <span className="text-xs text-muted-foreground">Photo or Document</span>
                </div>
            ) : (
                <div className="relative w-full p-3 bg-muted/30 rounded-xl border border-border flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                        {file.type.startsWith("image/") ? <FileImage className="w-4 h-4 text-primary" /> : <FileText className="w-4 h-4 text-primary" />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">{file.name}</p>
                        <p className="text-[10px] text-muted-foreground">Uploaded</p>
                    </div>
                    <button type="button" onClick={() => type === 'invoice' ? setInvoiceFile(null) : setWarrantyFile(null)} className="p-1 hover:bg-muted rounded-full">
                        <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent side="bottom" onOpenAutoFocus={(e) => e.preventDefault()} className="max-h-[85vh] rounded-t-3xl flex flex-col p-0" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
                <SheetHeader className="pb-4 flex-shrink-0 px-6 pt-8">
                    <SheetTitle className="text-xl font-bold text-foreground">{initialData ? "Edit Furniture / Item" : "Add Furniture / Item"}</SheetTitle>
                </SheetHeader>

                <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 space-y-4 pb-4 px-6">
                    <div className="space-y-2">
                        <Label htmlFor="item-name">Item Name *</Label>
                        <Input id="item-name" value={name} onChange={(e) => setName(e.target.value)} onFocus={handleFocus} placeholder="e.g. Leather Sofa" className="rounded-xl" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="item-brand">Brand</Label>
                        <Input id="item-brand" value={brand} onChange={(e) => setBrand(e.target.value)} onFocus={handleFocus} placeholder="e.g. Coricraft" className="rounded-xl" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="item-notes">Care Instuctions / Notes</Label>
                        <Textarea id="item-notes" value={notes} onChange={(e) => setNotes(e.target.value)} onFocus={handleFocus} placeholder="e.g. Clean with specific leather cream only..." className="rounded-xl resize-none" rows={3} />
                    </div>
                    <div className="space-y-4 pt-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2 min-w-0">
                                <Label htmlFor="purchase-price">Purchase Price</Label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium">R</span>
                                    <Input
                                        id="purchase-price"
                                        type="number"
                                        value={purchasePrice}
                                        onChange={(e) => setPurchasePrice(e.target.value)}
                                        onFocus={handleFocus}
                                        className="rounded-xl pl-7"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2 min-w-0">
                                <Label>Purchase Date</Label>
                                <Input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} onFocus={handleFocus} className="rounded-xl w-full" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Warranty Expiry</Label>
                            <Input type="date" value={warrantyExpiry} onChange={(e) => setWarrantyExpiry(e.target.value)} onFocus={handleFocus} className="rounded-xl w-full" />
                        </div>
                    </div>

                    <div className="space-y-4 pt-2">
                        {renderFileUpload("Invoice / Receipt", invoiceFile, "invoice")}
                        {renderFileUpload("Warranty Doc", warrantyFile, "warranty")}
                    </div>

                    {/* Routine Maintenance Sub-Tasks */}
                    <div className="pt-4 border-t border-border">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-medium text-muted-foreground">
                                Routine Maintenance Tasks
                            </h3>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setMaintenanceTasks(prev => [
                                        ...prev,
                                        {
                                            id: Math.random().toString(36).substr(2, 9),
                                            name: "",
                                            frequency: "",
                                            frequency_unit: "months",
                                            last_service_date: ""
                                        }
                                    ]);
                                }}
                                className="h-8 rounded-lg gap-1 text-xs border-primary/20 bg-primary/5 text-primary font-semibold"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Add Task
                            </Button>
                        </div>

                        {maintenanceTasks.length === 0 ? (
                            <p className="text-xs text-muted-foreground italic px-1">
                                No custom sub-tasks added yet. Add tasks like oiling or tightening bolts.
                            </p>
                        ) : (
                            <div className="space-y-4">
                                {maintenanceTasks.map((task) => (
                                    <div key={task.id} className="p-3 border border-border rounded-2xl bg-muted/20 relative space-y-3">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setMaintenanceTasks(prev => prev.filter(t => t.id !== task.id));
                                            }}
                                            className="absolute top-2 right-2 p-1 hover:bg-muted rounded-full text-muted-foreground hover:text-destructive transition-colors"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>

                                        <div className="space-y-1.5 pr-6">
                                            <Label className="text-xs font-semibold">Task Name</Label>
                                            <Input
                                                value={task.name}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setMaintenanceTasks(prev => prev.map(t => t.id === task.id ? { ...t, name: val } : t));
                                                }}
                                                placeholder="e.g., Oil Leather, Tighten Screws"
                                                className="h-9 rounded-xl text-sm w-full"
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="space-y-1.5 min-w-0">
                                                <Label className="text-xs font-semibold">Frequency</Label>
                                                <Input
                                                    type="number"
                                                    value={task.frequency}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setMaintenanceTasks(prev => prev.map(t => t.id === task.id ? { ...t, frequency: val } : t));
                                                    }}
                                                    placeholder="e.g., 7, 2"
                                                    className="h-9 rounded-xl text-sm w-full"
                                                />
                                            </div>
                                            <div className="space-y-1.5 min-w-0">
                                                <Label className="text-xs font-semibold">Unit</Label>
                                                <Select
                                                    value={task.frequency_unit}
                                                    onValueChange={(val) => {
                                                        setMaintenanceTasks(prev => prev.map(t => t.id === task.id ? { ...t, frequency_unit: val } : t));
                                                    }}
                                                >
                                                    <SelectTrigger className="h-9 rounded-xl text-sm w-full">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="days">Days</SelectItem>
                                                        <SelectItem value="weeks">Weeks</SelectItem>
                                                        <SelectItem value="months">Months</SelectItem>
                                                        <SelectItem value="years">Years</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>

                                        <div className="space-y-1.5 min-w-0">
                                            <Label className="text-xs font-semibold">Last Done Date (Optional)</Label>
                                            <Input
                                                type="date"
                                                value={task.last_service_date || ""}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setMaintenanceTasks(prev => prev.map(t => t.id === task.id ? { ...t, last_service_date: val } : t));
                                                }}
                                                className="h-9 rounded-xl text-sm w-full"
                                            />
                                        </div>
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setMaintenanceTasks(prev => [
                                            ...prev,
                                            {
                                                id: Math.random().toString(36).substr(2, 9),
                                                name: "",
                                                frequency: "",
                                                frequency_unit: "months",
                                                last_service_date: ""
                                            }
                                        ]);
                                    }}
                                    className="w-full h-10 rounded-xl gap-2 border-dashed border-2 text-primary font-semibold hover:bg-primary/5"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Another Task
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex-shrink-0 pt-2 px-6">
                    <Button onClick={handleSave} className="w-full rounded-xl py-5 text-white" disabled={loading || uploading}>
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (initialData ? "Save Changes" : "Save to Home")}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default AddFurnitureSheet;
